import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Globe2, MessageCircle, Users, Euro, TrendingUp, RefreshCw, Loader2 } from "lucide-react";
import { T } from "@/locales/tr";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = {
  "papicoo.com": "#0F291E",
  "papicoo.fr": "#D4AF37",
  "widget": "#8B5A3C",
  "LinkedIn": "#0A66C2",
  "papicoo_fr_form": "#D4AF37",
  "Instagram": "#E1306C",
  "Make": "#6D5DFE",
  "default": "#7c7060",
};

const sourceColor = (s) => COLORS[s] || COLORS.default;
const fmtEur = (n) => `${(Number(n) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €`;

export default function SourcesTab({ password }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/sources/stats?days=${days}`, {
        headers: { "X-Admin-Password": password },
      });
      setData(r.data);
    } catch (e) {
      toast.error(T("Impossible de charger les sources"));
    } finally {
      setLoading(false);
    }
  }, [days, password]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[color:var(--papicoo-gold)]" /></div>;
  if (!data) return null;

  const T = data.totals;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h3 className="font-display text-2xl text-[color:var(--papicoo-ink)] flex items-center gap-2">
          <Globe2 size={22} className="text-[color:var(--papicoo-gold)]" />
          {T("Sources & conversion")}
        </h3>
        <div className="flex items-center gap-3">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            data-testid="sources-period-select"
            className="px-3 py-1.5 rounded-lg border border-[color:var(--papicoo-border)] bg-white text-sm"
          >
            <option value={7}>{T("7 derniers jours")}</option>
            <option value={30}>{T("30 derniers jours")}</option>
            <option value={90}>{T("90 derniers jours")}</option>
          </select>
          <button
            onClick={load}
            data-testid="sources-refresh-btn"
            className="papicoo-btn-primary px-3 py-1.5 text-sm flex items-center gap-1.5"
          >
            <RefreshCw size={14} /> {T("Actualiser")}
          </button>
        </div>
      </div>

      {/* KPIs globaux */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={<MessageCircle size={18} />} label={T("Conversations")} value={T.chat_sessions} accent="ink" />
        <KpiCard icon={<Users size={18} />} label={T("Prospects")} value={T.prospects} accent="gold" sub={`${T.conversion_rate_pct}% conversion`} />
        <KpiCard icon={<Euro size={18} />} label={T("Revenu")} value={fmtEur(T.revenue_eur)} accent="ink" sub={`${T.transactions} ventes`} />
        <KpiCard icon={<TrendingUp size={18} />} label={T("Make calls")} value={T.make_webhook_calls} accent="gold" sub={`${T.papicoo_fr_contact_forms} formulaires fr`} />
      </div>

      {/* Chat sessions par source */}
      <Section title={T("💬 Conversations chat")} data={data.chat_sessions_by_source} keyName="source" valueName="sessions" />

      {/* Prospects par plateforme */}
      <Section title={T("🌱 Prospects par plateforme")} data={data.prospects_by_platform} keyName="platform" valueName="count" />

      {/* Transactions par source */}
      <Section
        title={T("💶 Ventes par source")}
        data={data.transactions_by_source}
        keyName="source"
        valueName="count"
        extra={(row) => fmtEur(row.amount_eur)}
      />
    </div>
  );
}

function KpiCard({ icon, label, value, accent, sub }) {
  const accentClass = accent === "gold" ? "text-[color:var(--papicoo-gold)]" : "text-[color:var(--papicoo-ink)]";
  return (
    <div className="papicoo-card p-5">
      <div className={`flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[color:var(--papicoo-muted)] ${accentClass}`}>
        {icon}
        <span>{label}</span>
      </div>
      <div className="font-display text-3xl text-[color:var(--papicoo-ink)] mt-3">{value}</div>
      {sub && <div className="text-xs text-[color:var(--papicoo-muted)] mt-1">{sub}</div>}
    </div>
  );
}

function Section({ title, data, keyName, valueName, extra }) {
  if (!data || data.length === 0) {
    return (
      <div className="papicoo-card p-5">
        <h4 className="font-display text-lg text-[color:var(--papicoo-ink)] mb-3">{title}</h4>
        <p className="text-sm text-[color:var(--papicoo-muted)]">{T("Aucune donnée sur la période.")}</p>
      </div>
    );
  }
  const max = Math.max(...data.map((d) => Number(d[valueName]) || 0), 1);
  return (
    <div className="papicoo-card p-5">
      <h4 className="font-display text-lg text-[color:var(--papicoo-ink)] mb-4">{title}</h4>
      <div className="space-y-2">
        {data.map((row) => {
          const v = Number(row[valueName]) || 0;
          const pct = (v / max) * 100;
          const label = row[keyName] || "—";
          return (
            <div key={`${label}-${v}`} className="text-sm">
              <div className="flex justify-between mb-1">
                <span className="font-medium text-[color:var(--papicoo-ink)]">{label}</span>
                <span className="text-[color:var(--papicoo-muted)]">
                  {v}{extra ? ` · ${extra(row)}` : ""}
                </span>
              </div>
              <div className="h-2 bg-[color:var(--papicoo-sand-deep)] rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${pct}%`, background: sourceColor(label) }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
